import React from "react";
import { Redirect } from "react-router-dom";

import { ErrorPage } from "@/ErrorPage";
import { FEATURES } from "@/features/registry";
import { FeatureSplashLoadingPlaceholder } from "@/splash/FeatureSplashLoadingPlaceholder";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import type { Feature } from "@/types/feature";

export interface FeatureChildRouteGuardProps {
  feature: Feature;
  children: React.ReactNode;
  // Lets a trusted deep-link bypass the Get Started engagement splash without
  // weakening the RBAC, unlock, or AI-consent gates. Used by the AI chat
  // "Open in Survey Builder" entry point (CE-1740) — see SurveysPage.
  skipEngagementGate?: boolean;
}

export function FeatureChildRouteGuard({
  feature,
  children,
  skipEngagementGate = false,
}: FeatureChildRouteGuardProps) {
  const access = useFeatureAccess(feature);

  switch (access.state) {
    case "loading":
      return <FeatureSplashLoadingPlaceholder />;
    case "coming-soon":
      return <ErrorPage />;
    case "no-permissions":
    case "needs-ai-consent":
      return <Redirect to={FEATURES[feature].rootPath} />;
    case "get-started":
      if (skipEngagementGate) {
        return <>{children}</>;
      }
      return <Redirect to={FEATURES[feature].rootPath} />;
    case "feature":
      return <>{children}</>;
  }
}
